import { Card, CardContent, Typography, Box, Chip, alpha } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';

interface CompactMetricCardProps {
  title: string;
  value: string | number;
  value2?: string | number;
  subtitle?: string;
  icon: SvgIconComponent;
  trend?: {
    value: string | number;
    isPositive: boolean;
  };
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'custom' | 'custom1' | 'custom2' | 'custom_summary_card';
  loading?: boolean;
}

const CompactMetricCard = ({
  title,
  value,
  value2,
  subtitle,
  icon: Icon,
  trend,
  color = 'primary',
  loading = false,
}: CompactMetricCardProps) => {
  const getColorConfig = () => {
    switch (color) {
      case 'primary':
        return {
          background: 'linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 100%)',
          borderColor: '#BFDBFE',
          iconBg: '#3B82F6',
          iconColor: '#FFFFFF',
          textColor: '#1E40AF',
          subTextColor: '#60A5FA',
          shadow: '0 4px 12px rgba(59, 130, 246, 0.08)',
          hoverBorder: '#3B82F6',
        };
      case 'secondary':
      case 'success':
        return {
          background: 'linear-gradient(135deg, #F0FDF4 0%, #FFFFFF 100%)',
          borderColor: '#BBF7D0',
          iconBg: '#10B981',
          iconColor: '#FFFFFF',
          textColor: '#14532D',
          subTextColor: '#4ADE80',
          shadow: '0 4px 12px rgba(16, 185, 129, 0.08)',
          hoverBorder: '#10B981',
        };
      case 'error':
        return {
          background: 'linear-gradient(135deg, #FEF2F2 0%, #FFFFFF 100%)',
          borderColor: '#FECACA',
          iconBg: '#EF4444',
          iconColor: '#FFFFFF',
          textColor: '#7F1D1D',
          subTextColor: '#F87171',
          shadow: '0 4px 12px rgba(239, 68, 68, 0.08)',
          hoverBorder: '#EF4444',
        };
      case 'warning':
        return {
          background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)',
          borderColor: '#FDE68A',
          iconBg: '#F59E0B',
          iconColor: '#FFFFFF',
          textColor: '#78350F',
          subTextColor: '#FBBF24',
          shadow: '0 4px 12px rgba(245, 158, 11, 0.08)',
          hoverBorder: '#F59E0B',
        };
      case 'info':
        return {
          background: 'linear-gradient(135deg, #F0F9FF 0%, #FFFFFF 100%)',
          borderColor: '#BAE6FD',
          iconBg: '#0EA5E9',
          iconColor: '#FFFFFF',
          textColor: '#0C4A6E',
          subTextColor: '#38BDF8',
          shadow: '0 4px 12px rgba(14, 165, 233, 0.08)',
          hoverBorder: '#0EA5E9',
        };
      case 'custom':
        return {
          background: 'linear-gradient(135deg, #F0F9FF 0%, #FFFFFF 100%)',
          borderColor: '#BAE6FD',
          iconBg: '#7a88b6ff',
          iconColor: '#FFFFFF',
          textColor: '#0a112bff',
          subTextColor: '#40768dff',
          shadow: '0 4px 12px rgba(14, 165, 233, 0.08)',
          hoverBorder: '#7a88b6ff',
        };
      case 'custom1':
        return {
          background: 'linear-gradient(135deg, #F0F9FF 0%, #FFFFFF 100%)',
          borderColor: '#7c3aed',
          iconBg: '#7c3aed',
          iconColor: '#FFFFFF',
          textColor: '#0a112bff',
          subTextColor: '#40768dff',
          shadow: '0 4px 12px rgba(14, 165, 233, 0.08)',
          hoverBorder: '#7655b1ff',
        };
      case 'custom2':
        return {
          background: 'linear-gradient(135deg, #F0F9FF 0%, #FFFFFF 100%)',
          borderColor: '#12f5cfec',
          iconBg: '#12f5cfec',
          iconColor: '#FFFFFF',
          textColor: '#0f2836ff',
          subTextColor: '#1c4b43d3',
          shadow: '0 4px 12px rgba(14, 165, 233, 0.08)',
          hoverBorder: '#12f5cfec',
        };
case 'custom_summary_card':
        return {
          // Soft blue gradient background
          background: 'linear-gradient(135deg, #E0F2FE 0%, #FFFFFF 100%)', 
          // Strong Blue accents
          borderColor: '#0288D1', 
          iconBg: '#0288D1',
          iconColor: '#FFFFFF',
          // Deep slate/navy for text readability
          textColor: '#0F172A', 
          subTextColor: '#475569',
          // Shadow updated to match the blue tone
          shadow: '0 4px 12px rgba(2, 136, 209, 0.12)',
          hoverBorder: '#01579B', // Slightly darker blue on hover
        };
      default:
        return {
          background: 'linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%)',
          borderColor: '#E2E8F0',
          iconBg: '#64748B',
          iconColor: '#FFFFFF',
          textColor: '#1E293B',
          subTextColor: '#94A3B8',
          shadow: '0 4px 12px rgba(100, 116, 139, 0.08)',
          hoverBorder: '#64748B',
        };
    }
  };

  const config = getColorConfig();

  if (loading) {
    return (
      <Card
        sx={{
          background: config.background,
          border: `1px solid ${config.borderColor}`,
          borderRadius: 3,
          boxShadow: config.shadow,
          height: 112,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ textAlign: 'center', width: '100%' }}>
          <Typography variant="body2" sx={{ color: config.textColor, opacity: 0.7 }}>
            Loading...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        background: config.background,
        border: `1px solid ${config.borderColor}`,
        borderRadius: { xs: 2, sm: 3, md: 3 },
        boxShadow: config.shadow,
        minHeight: {
              xs: 88,
              sm: 96,
              md: 104,
              lg: 112,
              xl: 118,
            },
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 24px -4px ${alpha(config.iconBg, 0.15)}`,
          borderColor: config.hoverBorder,
          '& .icon-box': {
            transform: 'scale(1.1) rotate(5deg)',
          },
        },
      }}
    >

      <CardContent
                sx={{
                  width: '100%',
                  p: {
                    xs: 1,
                    sm: 1.25,
                    md: 1.5,
                    lg: 1.75,
                    xl: 2,
                  },
                  '&:last-child': {
                    pb: {
                      xs: 1,
                      sm: 1.25,
                      md: 1.5,
                      lg: 1.75,
                      xl: 2,
                    },
                  },
                }}
              >

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' }}>
          <Box sx={{ flex: 1, minWidth: 0, mr: 1.25 }}>
            <Typography
              variant="subtitle2"
              noWrap
              sx={{
                fontWeight: 600,
                mb: 0.5,
                // Keep title color consistent across the app (do not vary by card "color")
                color: 'text.secondary',
                fontSize: { xs: '0.58rem', sm: '0.62rem', md: '0.66rem' },
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              title={typeof value === 'string' ? value : String(value)}
              sx={{
                fontWeight: 700,
                mb: 0.25,
                color: config.textColor,
                fontSize: { xs: '0.78rem', sm: '0.86rem', md: '0.94rem', lg: '1rem', xl: '1.04rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </Typography>

            <Typography
              variant="h4"
              title={typeof value2 === 'string' ? value2 : String(value)}
              sx={{
                color: alpha(config.textColor, 0.6),
                fontSize: { xs: '0.62rem', sm: '0.66rem', md: '0.7rem' },
                fontWeight: 500,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {value2}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              {trend && (
                <Chip
                  label={typeof trend.value === 'string' ? trend.value : `${trend.isPositive ? '+' : ''}${trend.value}%`}
                  size="small"
                  sx={{
                    backgroundColor: trend.isPositive ? alpha('#166534', 0.1) : alpha('#991b1b', 0.1),
                    color: trend.isPositive ? '#166534' : '#991b1b',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: 24,
                    borderRadius: '6px',
                    flexShrink: 0,
                    '& .MuiChip-label': { px: 1 }
                  }}
                />
              )}
              {subtitle && (
                <Typography
                  variant="caption"
                  noWrap
                  title={subtitle}
                  sx={{
                    color: alpha(config.textColor, 0.6),
                    fontSize: { xs: '0.62rem', sm: '0.66rem', md: '0.7rem' },
                    fontWeight: 500,
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            className="icon-box"
            sx={{
                 width: {
                  xs: 28,
                  sm: 32,
                  md: 36,
                  lg: 40,
                  xl: 44,
                },
                height: {
                  xs: 28,
                  sm: 32,
                  md: 36,
                  lg: 40,
                  xl: 44,
                },
              background: `linear-gradient(135deg, ${config.iconBg}, ${alpha(config.iconBg, 0.8)})`,
              borderRadius: {
                xs: '10px',
                sm: '12px',
                md: '14px',
                lg: '16px',
                xl: '18px',
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: {
                xs: `0 4px 10px -2px ${alpha(config.iconBg, 0.3)}`,
                md: `0 6px 14px -3px ${alpha(config.iconBg, 0.35)}`,
                lg: `0 8px 16px -4px ${alpha(config.iconBg, 0.4)}`,
              },
              transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <Icon sx={{ fontSize: { xs: 14, sm: 16, md: 18, lg: 20, xl: 22 }, color: config.iconColor }} />
   
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CompactMetricCard;
